import React, { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useProjects } from '../../hooks/useProjects.jsx'
import api from '../../services/api'
import { Loader2 } from 'lucide-react'
import { formatDate } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

/**
 * Consultant Recommendations — real data from GET /consultant/recommendations/.
 * Consultant creates new recommendations via POST /consultant/recommendations/.
 */
function Recommendations() {
  const { t } = useTranslation('consultants')
  const { t: tc } = useTranslation('common')
  const { user } = useAuth()
  const { projects } = useProjects()
  const [recommendations, setRecommendations] = useState([])
  const [loading, setLoading] = useState(true)
  const [showNew, setShowNew] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [form, setForm] = useState({
    project_id: '', title: '', description: '', type: 'Technical',
    priority: 'Medium', estimated_impact: 'Medium', implementation_cost: '', timeframe: '',
  })

  const projectName = (id) => projects.find(p => p.id === id)?.name || `Project #${id}`
  const myProjects = projects.filter(p => p.consultant_id === user?.id || p.consultantId === user?.id)

  const load = () => {
    setLoading(true)
    api.get('/consultant/recommendations/')
      .then(r => setRecommendations(r.data || []))
      .catch(() => setRecommendations([]))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.project_id) { setError('Please select a project.'); return }
    setSaving(true); setError(null)
    try {
      await api.post('/consultant/recommendations/', {
        title: form.title,
        description: form.description,
        type: form.type,
        priority: form.priority,
        estimated_impact: form.estimated_impact,
        implementation_cost: parseFloat(form.implementation_cost) || 0,
        timeframe: form.timeframe || null,
        project_id: parseInt(form.project_id),
      })
      setShowNew(false)
      setForm({ project_id: '', title: '', description: '', type: 'Technical', priority: 'Medium', estimated_impact: 'Medium', implementation_cost: '', timeframe: '' })
      load()
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit recommendation')
    } finally { setSaving(false) }
  }

  const statusColor = (s) => ({
    Approved: 'bg-green-100 text-green-800 border-green-200',
    'Under Review': 'bg-blue-100 text-blue-800 border-blue-200',
    Rejected: 'bg-red-100 text-red-800 border-red-200',
  }[s] || 'bg-gray-100 text-gray-800 border-gray-200')

  const priorityColor = (p) => ({
    High: 'bg-red-100 text-red-800', Medium: 'bg-yellow-100 text-yellow-800', Low: 'bg-green-100 text-green-800',
  }[p] || 'bg-gray-100 text-gray-800')

  const stats = {
    total: recommendations.length,
    approved: recommendations.filter(r => r.status === 'Approved').length,
    pending: recommendations.filter(r => r.status === 'Under Review').length,
    totalValue: recommendations.reduce((s, r) => s + (r.implementation_cost || 0), 0),
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-7 h-7 text-purple-600 animate-spin" /></div>
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('recommendations')}</h1>
          <p className="mt-1 text-gray-600">{t('manage_recommendations')}</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary">+ New Recommendation</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6"><p className="text-sm font-medium text-gray-600">{t('total_recommendations')}</p><p className="text-2xl font-bold text-gray-900">{stats.total}</p></div>
        <div className="card p-6"><p className="text-sm font-medium text-gray-600">{t('rec_approved')}</p><p className="text-2xl font-bold text-gray-900">{stats.approved}</p></div>
        <div className="card p-6"><p className="text-sm font-medium text-gray-600">{t('rec_under_review')}</p><p className="text-2xl font-bold text-gray-900">{stats.pending}</p></div>
        <div className="card p-6"><p className="text-sm font-medium text-gray-600">{t('total_value')}</p><p className="text-2xl font-bold text-gray-900">${stats.totalValue.toLocaleString()}</p></div>
      </div>

      {/* List */}
      <div className="space-y-6">
        {recommendations.length === 0 ? (
          <div className="card"><div className="p-12 text-center">
            <h3 className="mt-4 text-lg font-medium text-gray-900">{t('no_recommendations')}</h3>
            <p className="mt-2 text-gray-600">Start providing valuable insights by creating your first recommendation.</p>
            <button onClick={() => setShowNew(true)} className="btn-primary mt-4">{t('create_first_rec')}</button>
          </div></div>
        ) : recommendations.map((r) => (
          <div key={r.id} className="card hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
                    <h3 className="text-xl font-semibold text-gray-900">{r.title}</h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusColor(r.status)}`}>{r.status}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColor(r.priority)}`}>{r.priority}</span>
                  </div>
                  <p className="text-sm text-blue-600 font-medium mb-2">{projectName(r.project_id)}</p>
                  <p className="text-gray-700 mb-4">{r.description}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs font-medium text-gray-600 mb-1">{tc('labels.category')}</p><p className="text-sm font-semibold text-gray-900">{r.type}</p></div>
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs font-medium text-gray-600 mb-1">{t('impact')}</p><p className="text-sm font-semibold text-gray-900">{r.estimated_impact || '—'}</p></div>
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs font-medium text-gray-600 mb-1">{t('cost')}</p><p className="text-sm font-semibold text-gray-900">${(r.implementation_cost || 0).toLocaleString()}</p></div>
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs font-medium text-gray-600 mb-1">{t('timeframe')}</p><p className="text-sm font-semibold text-gray-900">{r.timeframe || '—'}</p></div>
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs font-medium text-gray-600 mb-1">{t('rec_submitted')}</p><p className="text-sm font-semibold text-gray-900">{r.date_submitted ? formatDate(r.date_submitted) : '—'}</p></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* New Modal */}
      {showNew && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">{t('new_recommendation')}</h3>
              <button onClick={() => setShowNew(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            {error && <div className="mb-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('select_project')}</label>
                <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="form-input" required>
                  <option value="">{t('choose_project')}</option>
                  {(myProjects.length ? myProjects : projects).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{tc('labels.name')}</label>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="form-input" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{tc('labels.description')}</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="form-input" required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{tc('labels.category')}</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="form-input">
                    <option value="Technical">{t('type_technical')}</option><option value="Strategic">{t('type_strategic')}</option><option value="Process">{t('type_process')}</option><option value="Financial">{t('type_financial')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{tc('labels.priority')}</label>
                  <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="form-input">
                    <option value="Low">{tc('priority.low')}</option><option value="Medium">{tc('priority.medium')}</option><option value="High">{tc('priority.high')}</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('estimated_impact')}</label>
                  <select value={form.estimated_impact} onChange={(e) => setForm({ ...form, estimated_impact: e.target.value })} className="form-input">
                    <option value="Low">{tc('priority.low')}</option><option value="Medium">{tc('priority.medium')}</option><option value="High">{tc('priority.high')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('implementation_cost')}</label>
                  <input type="number" value={form.implementation_cost} onChange={(e) => setForm({ ...form, implementation_cost: e.target.value })} className="form-input" placeholder="0" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('timeframe')}</label>
                  <input type="text" value={form.timeframe} onChange={(e) => setForm({ ...form, timeframe: e.target.value })} className="form-input" placeholder={t('ph_timeframe')} />
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button type="button" onClick={() => setShowNew(false)} className="btn-secondary">{tc('actions.cancel')}</button>
                <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50">{saving ? 'Submitting…' : 'Submit Recommendation'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Recommendations
